/**
 * useConnection — health check polling for backend status.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import CONFIG from '../config';
import Logger from '../utils/logger';
import type { HealthCheckResponse } from '../types';

export interface ConnectionStatus {
  connected: boolean;
  model: string | null;
  lastChecked: number | null;
  error: string | null;
  gpuType: string | null;
  provider: string | null;
}

export function useConnection() {
  const [status, setStatus] = useState<ConnectionStatus>({
    connected: false,
    model: null,
    lastChecked: null,
    error: null,
    gpuType: null,
    provider: null,
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<boolean> | null>(null);

  const checkHealth = useCallback((): Promise<boolean> => {
    if (inFlightRef.current) return inFlightRef.current;

    const request = (async () => {
      try {
        const response = await fetch(`${CONFIG.API_URL}/health`, {
          method: 'GET',
          signal: AbortSignal.timeout(10_000),
        });

        if (!response.ok) throw new Error(`Health check failed: ${response.status}`);

        const data: HealthCheckResponse = await response.json();
        setStatus({
          connected: true,
          model: data.model ?? null,
          lastChecked: Date.now(),
          error: null,
          gpuType: data.gpu_type ?? null,
          provider: data.provider_id ?? null,
        });
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        Logger.warn('Health check failed:', message);
        setStatus((prev) => ({
          ...prev,
          connected: false,
          lastChecked: Date.now(),
          error: message,
        }));
        return false;
      }
    })();

    inFlightRef.current = request;
    void request.finally(() => {
      if (inFlightRef.current === request) inFlightRef.current = null;
    });
    return request;
  }, []);

  // Start/stop polling
  useEffect(() => {
    let cancelled = false;
    let retryIndex = 0;
    const retryDelays = CONFIG.HEALTH_RETRY_DELAYS_MS as readonly number[];

    const clearRetry = () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    };

    const probeWithRecovery = async () => {
      const connected = await checkHealth();
      if (cancelled) return;
      if (connected) {
        retryIndex = 0;
        clearRetry();
        return;
      }
      const retryDelay = retryDelays[retryIndex];
      if (retryDelay === undefined) return;
      retryIndex += 1;
      clearRetry();
      retryTimerRef.current = setTimeout(() => void probeWithRecovery(), retryDelay);
    };

    const startRecoveryCycle = () => {
      retryIndex = 0;
      clearRetry();
      void probeWithRecovery();
    };

    // Initial check after a short render delay. Failures retry quickly for cold
    // starts, then fall back to the ordinary 30-second health cadence.
    const initialTimer = setTimeout(startRecoveryCycle, 100);

    // Periodic polling
    intervalRef.current = setInterval(
      startRecoveryCycle,
      CONFIG.HEALTH_CHECK_INTERVAL_MS
    );

    return () => {
      cancelled = true;
      clearTimeout(initialTimer);
      clearRetry();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkHealth]);

  return { status, checkHealth };
}

export default useConnection;
